import { type ComponentType, type FC, Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import {
    Anchor,
    Body1,
    Heading1,
    Stack,
} from '@myphx-engineering/phx-design-system';

import {
    useFillPathway,
    useFillPrescription,
} from '../../../contexts/fill/use-fill-prescription';
import { useDrugKey } from '../../../hooks/use-drug-key';
import { usePathwayOptions } from '../../../hooks/use-pathway-options';
import { useProgramTerms } from '../../../hooks/use-program-terms';
import { ShortProgramTerms } from '../../programs/ShortProgramTerms';
import { ConditionalComponent } from '../../utility/ConditionalComponent';

import { ConfigurablePathSelectionCards } from './ConfigurablePathSelectionCards';
import { InsuranceCoverageDescription } from './insurance/InsuranceCoverageDescription';
import { InsurancePathSelectionCardsV2 } from './insurance/InsurancePathSelectionCardsV2';
import { SelfPayCoverageDescription } from './self-pay/SelfPayCoverageDescription';
import { SelfPayPathSelectionCards } from './self-pay/SelfPayPathSelectionCards';

export const PathSelectionScreenV2: FC = () => {
    const { t } = useTranslation('lilly');

    const { medication } = useFillPrescription();
    const { pathway } = useFillPathway();

    const drugKey = useDrugKey(medication);
    const pathwayOptions = usePathwayOptions();

    const selfPayTerms = useProgramTerms(medication, 'self-pay');
    const insuranceTerms = useProgramTerms(medication, 'insurance');

    let CoverageDescription: ComponentType = Fragment,
        PathSelectionCards: ComponentType = Fragment;

    switch (pathway) {
        case 'self-pay':
            CoverageDescription = SelfPayCoverageDescription;
            PathSelectionCards = SelfPayPathSelectionCards;
            break;
        case 'insurance':
            CoverageDescription = InsuranceCoverageDescription;
            PathSelectionCards = InsurancePathSelectionCardsV2;
            break;
        case 'fallback':
            break;
    }

    return (
        <Stack gap="lg" data-testid="path-select-page-root">
            <ConditionalComponent condition={!!pathwayOptions}>
                <Stack gap="xxs">
                    <ConditionalComponent condition={!!pathwayOptions?.heading}>
                        <Heading1 data-testid="path-select-title">
                            {pathwayOptions?.heading}
                        </Heading1>
                    </ConditionalComponent>
                    <ConditionalComponent
                        condition={!!pathwayOptions?.description}
                    >
                        <Body1 data-testid="path-select-description">
                            <Trans
                                ns="dynamic"
                                components={{
                                    a: (
                                        <Anchor
                                            href={
                                                pathwayOptions?.descriptionLinkUrl ??
                                                '#'
                                            }
                                            target="_blank"
                                        />
                                    ),
                                }}
                            >
                                {pathwayOptions?.description}
                            </Trans>
                        </Body1>
                    </ConditionalComponent>
                </Stack>
                <ConfigurablePathSelectionCards
                    options={pathwayOptions?.cards ?? []}
                />
            </ConditionalComponent>
            <ConditionalComponent condition={!pathwayOptions}>
                <Heading1 data-testid="path-select-title">
                    {t('fill.pathSelectV2.title')}
                </Heading1>
                <CoverageDescription />
                <PathSelectionCards />
            </ConditionalComponent>
            <ConditionalComponent condition={pathway === 'insurance'}>
                <ShortProgramTerms
                    drugKey={drugKey}
                    pathway="insurance"
                    terms={insuranceTerms}
                />
            </ConditionalComponent>
            <ShortProgramTerms
                drugKey={drugKey}
                pathway="self-pay"
                terms={selfPayTerms}
            />
        </Stack>
    );
};
